import { getConfigForMeasuredTest, getConfigForTest } from 'Components/Worksheets/Config';
import {
  mergeConfig,
  measuredUsingConfigFor as wrap,
  wrapAsDerivedFromMeasurementsConfig,
} from 'Components/Worksheets/Core/Helpers/configHelpers';
import qestIds from 'Components/Worksheets/QestIds';
import { pathAsArray, pathAsString } from 'Lib/Object/Path/shared';
import getLabelConfig from './labelConfig';

const getConfig = (prefix: string) =>
  mergeConfig(
    {
      [`${prefix}.Preparation.MassRetainedOn20_0mmSievePercentage`]: { format: { nearest: 0.1 } },
      [`${prefix}.Preparation.DryMassRetainedOn20_0mmSievePercentage`]: { format: { nearest: 0.1 } },

      ...getConfigForMeasuredTest(
        qestIds.moistureContent.BS_EN_ISO_17892_1.rev2014,
        `${prefix}.Preparation.EmbeddedWaterContentTest`
      ),
      ...getConfigForTest(
        qestIds.moistureConditionValue.BS_1377_2_Clause_13_4.rev2022,
        `${prefix}.EmbeddedTests.Tests.Data`
      ),
      [`${prefix}.EmbeddedTests.Tests.Data.Preparation.WaterAddedTargetPercent`]: { format: { nearest: 0.1 } },
      ...getConfigForTest(qestIds.handShearVane.InHouseMethod, `${prefix}.EmbeddedTests.Tests.Data.HandShearVane`),
    },
    getLabelConfig(prefix),
    getMeasuredUsingConfig(prefix),
    getDerivedFromMeasurementsConfig(prefix),
    {
      [`${prefix}.Preparation.Balance`]: { defaultUom: 'g', defaultReadability: '1' },
    }
  );

const getMeasuredUsingConfig = (prefix: string) => {
  const balance = wrap(`${prefix}.Preparation.Balance`);
  return {
    [`${prefix}.Preparation.TareMass`]: balance,
    [`${prefix}.Preparation.TareAndTotalSampleMass`]: balance,
    [`${prefix}.Preparation.MassRetainedOn20_0mmSieve`]: balance,
    [`${prefix}.Preparation.TotalSampleDryMass`]: balance,
    [`${prefix}.EmbeddedTests.Tests.Data.Preparation.TestPortionMass`]: balance,

    [`${prefix}.EmbeddedTests.Tests.Data.Preparation.EmbeddedWaterContentTest.MeasuredTest.DryingTray.DryMassReadings.DrySoilAndContainerMass`]:
      ({ name }: { name: string }) =>
        wrap(pathAsString([...pathAsArray(name).slice(0, -4), 'Equipment.DryMassBalance'])),

    [`${prefix}.EmbeddedTests.Tests.Data.Preparation.EmbeddedWaterContentTest.MeasuredTest.DryingTray.ContainerMass`]:
      ({ name }: { name: string }) => wrap(pathAsString([...pathAsArray(name).slice(0, -2), 'Equipment.Balance'])),

    [`${prefix}.EmbeddedTests.Tests.Data.Preparation.EmbeddedWaterContentTest.MeasuredTest.DryingTray.WetSoilAndContainerMass`]:
      ({ name }: { name: string }) => wrap(pathAsString([...pathAsArray(name).slice(0, -2), 'Equipment.Balance'])),
  };
};

const getDerivedFromMeasurementsConfig = (prefix: string) => ({
  [`${prefix}.Preparation.TotalSampleMass`]: wrapAsDerivedFromMeasurementsConfig([
    `${prefix}.Preparation.TareMass`,
    `${prefix}.Preparation.TareAndTotalSampleMass`,
  ]),
  [`${prefix}.EmbeddedTests.Tests.Data.Preparation.EmbeddedWaterContentTest.MeasuredTest.DryingTray.WetSoilMass`]: ({
    name,
  }: {
    name: string;
  }) =>
    wrapAsDerivedFromMeasurementsConfig([
      pathAsString([...pathAsArray(name).slice(0, -1), 'ContainerMass']),
      pathAsString([...pathAsArray(name).slice(0, -1), 'WetSoilAndContainerMass']),
    ]),

  [`${prefix}.EmbeddedTests.Tests.Data.Preparation.EmbeddedWaterContentTest.MeasuredTest.DryingTray.ConstantDrySoilMass`]:
    ({ name }: { name: string }) =>
      wrapAsDerivedFromMeasurementsConfig([
        pathAsString([...pathAsArray(name).slice(0, -1), 'ContainerMass']),
        pathAsString([...pathAsArray(name).slice(0, -1), 'DryMassReadings[0].DrySoilAndContainerMass']),
      ]),
});

export { getConfig };

export default {
  [qestIds.moistureConditionValue.BS_1377_2_Clause_13_5.rev2022]: getConfig,
};
